import React, { useState, useEffect, useRef, useCallback } from 'react';
import styled from 'styled-components';
import { useAuth } from '../contexts/AuthContext';
import { useSocket } from '../contexts/SocketContext';
import { apiClient } from '../utils/api';
import { Auction, Team, Player } from '../types';
import { 
  Card, 
  Button, 
  Input,
  colors,
  spacing,
  typography,
  borderRadius,
  media
} from '../styles/GlobalStyles';
import { 
  Gavel,
  User,
  Timer
} from 'lucide-react';
import toast from 'react-hot-toast';
import TeamAssignment from './TeamAssignment';

// Socket event types
interface AuctionStartedData {
  auction: Auction;
}

interface BidPlacedData {
  auction: Auction;
  timeLeft: number;
  bidderName: string;
  bidAmount: number;
}

interface AuctionCompletedData {
  winnerName: string;
  playerName: string;
  winningBid: number;
}

const AuctionContainer = styled(Card)`
  padding: ${spacing[4]};
  margin-bottom: ${spacing[4]};
  border: 2px solid ${colors.green};
  background: #000000;

  ${media.tablet} {
    padding: ${spacing[6]};
    margin-bottom: ${spacing[6]};
  }
`;

const AuctionHeader = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: ${spacing[3]};
  margin-bottom: ${spacing[4]};

  ${media.tablet} {
    flex-direction: row;
    justify-content: space-between;
    gap: 0;
  }
`;

const AuctionTitle = styled.h2`
  font-size: ${typography.fontSize.xl};
  font-weight: ${typography.fontWeight.bold};
  color: ${colors.white};
  display: flex;
  align-items: center;
  gap: ${spacing[2]};
`;

const TimerDisplay = styled.div<{ timeLeft: number }>`
  display: flex;
  align-items: center;
  gap: ${spacing[2]};
  padding: ${spacing[3]} ${spacing[4]};
  background: ${props => {
    if (props.timeLeft <= 10) return colors.error + '20';
    if (props.timeLeft <= 30) return colors.warning + '20';
    return colors.success + '20';
  }};
  border: 1px solid ${props => {
    if (props.timeLeft <= 10) return colors.error;
    if (props.timeLeft <= 30) return colors.warning;
    return colors.success;
  }};
  border-radius: ${borderRadius.md};
  color: ${props => {
    if (props.timeLeft <= 10) return colors.error;
    if (props.timeLeft <= 30) return colors.warning;
    return colors.success;
  }};
  font-weight: ${typography.fontWeight.bold};
`;

const PlayerInfo = styled.div`
  display: flex;
  flex-direction: column;
  gap: ${spacing[3]};
  margin-bottom: ${spacing[4]};
  padding: ${spacing[3]};
  background: #000000;
  border-radius: ${borderRadius.md};

  ${media.tablet} {
    flex-direction: row;
    align-items: center;
    gap: ${spacing[4]};
    padding: ${spacing[4]};
  }
`;

const PlayerDetails = styled.div`
  flex: 1;
`;

const PlayerName = styled.h3`
  font-size: ${typography.fontSize.lg};
  font-weight: ${typography.fontWeight.bold};
  color: ${colors.white};
  margin-bottom: ${spacing[1]};
`;

const PlayerStats = styled.div`
  display: flex;
  flex-wrap: wrap;
  gap: ${spacing[2]};
  font-size: ${typography.fontSize.sm};
  color: ${colors.grey[400]};

  ${media.tablet} {
    gap: ${spacing[4]};
  }
`;

const PositionBadge = styled.span<{ position: string }>`
  padding: ${spacing[1]} ${spacing[2]};
  border-radius: ${borderRadius.base};
  font-size: ${typography.fontSize.xs};
  font-weight: ${typography.fontWeight.bold};
  color: ${colors.white};
  background: ${props => colors.position[props.position as keyof typeof colors.position] || colors.grey[600]};
`;

const CurrentBidSection = styled.div`
  text-align: center;
  margin-bottom: ${spacing[4]};
`;

const CurrentBidAmount = styled.div`
  font-size: ${typography.fontSize['3xl']};
  font-weight: ${typography.fontWeight.bold};
  color: ${colors.green};
  margin-bottom: ${spacing[2]};
`;

const CurrentBidder = styled.div`
  font-size: ${typography.fontSize.base};
  color: ${colors.grey[300]};
  margin-bottom: ${spacing[1]};
`;

const BidHistory = styled.div`
  max-height: 200px;
  overflow-y: auto;
  margin-bottom: ${spacing[4]};
`;

const BidItem = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: ${spacing[2]} ${spacing[3]};
  background: ${colors.grey[800]};
  border-radius: ${borderRadius.base};
  margin-bottom: ${spacing[2]};
  font-size: ${typography.fontSize.sm};
`;

const BidForm = styled.div`
  display: flex;
  flex-direction: column;
  gap: ${spacing[3]};
  align-items: stretch;

  ${media.tablet} {
    flex-direction: row;
    align-items: center;
  }
`;

const BidInput = styled(Input)`
  width: 100%;
  text-align: center;
  font-size: ${typography.fontSize.lg};
  font-weight: ${typography.fontWeight.bold};
  min-height: 52px;

  ${media.tablet} {
    width: 120px;
  }
`;

const QuickBidButtons = styled.div`
  display: flex;
  flex-wrap: wrap;
  gap: ${spacing[2]};
  margin-top: ${spacing[3]};
  justify-content: center;

  ${media.tablet} {
    justify-content: flex-start;
  }
`;

const QuickBidButton = styled(Button)`
  min-width: 60px;
  padding: ${spacing[2]} ${spacing[3]};

  @media (max-width: 767px) {
    flex: 1;
    min-width: 0;
  }
`;

const BidInsight = styled.div`
  display: flex;
  align-items: center;
  gap: ${spacing[2]};
  margin-top: ${spacing[3]};
  font-size: ${typography.fontSize.sm};
  color: ${colors.grey[300]};
`;

const InsightBadge = styled.span.withConfig({
  shouldForwardProp: (prop) => prop !== '$tier'
})<{ $tier: 'safe' | 'stretch' | 'overpay' | 'neutral' }>`
  display: inline-flex;
  align-items: center;
  border-radius: ${borderRadius.base};
  padding: 2px ${spacing[2]};
  font-size: ${typography.fontSize.xs};
  font-weight: ${typography.fontWeight.semibold};
  border: 1px solid ${props => {
    if (props.$tier === 'safe') return `${colors.success}66`;
    if (props.$tier === 'stretch') return `${colors.warning}66`;
    if (props.$tier === 'overpay') return `${colors.error}66`;
    return `${colors.grey[500]}66`;
  }};
  color: ${props => {
    if (props.$tier === 'safe') return colors.success;
    if (props.$tier === 'stretch') return colors.warning;
    if (props.$tier === 'overpay') return colors.error;
    return colors.grey[300];
  }};
  background: ${props => {
    if (props.$tier === 'safe') return `${colors.success}14`;
    if (props.$tier === 'stretch') return `${colors.warning}14`;
    if (props.$tier === 'overpay') return `${colors.error}14`;
    return `${colors.grey[500]}14`;
  }};
`;

const TeamsGrid = styled.div`
  display: grid;
  grid-template-columns: 1fr;
  gap: ${spacing[2]};
  max-height: 300px;
  overflow-y: auto;

  ${media.tablet} {
    grid-template-columns: repeat(auto-fit, minmax(200px, 1fr));
    max-height: 200px;
  }
`;



interface AuctionBiddingProps {
  draftId: string;
  teams: Team[];
  onAuctionComplete?: () => void;
}

const AuctionBidding: React.FC<AuctionBiddingProps> = ({ 
  draftId, 
  teams, 
  onAuctionComplete 
}) => {
  const { user } = useAuth();
  const { socket, isConnected } = useSocket();
  
  const [auction, setAuction] = useState<Auction | null>(null);
  const [bidAmount, setBidAmount] = useState<number>(1);
  const [timeLeft, setTimeLeft] = useState<number>(0);
  const [loading, setLoading] = useState(false);
  const [userTeam, setUserTeam] = useState<Team | null>(null);
  const [showTeamAssignment, setShowTeamAssignment] = useState(false);
  const [completedAuction, setCompletedAuction] = useState<Auction | null>(null);
  const auctionRef = useRef<Auction | null>(null);

  // Find user's team
  useEffect(() => {
    const team = teams.find(t => t.ownerId?._id === user?._id);
    setUserTeam(team || null);
  }, [teams, user]);

  const loadActiveAuction = useCallback(async () => {
    try {
      const response = await apiClient.auctions.getActive(draftId);
      if (response.success && response.data.auction) {
        setAuction(response.data.auction);
        setTimeLeft(response.data.auction.timeLeft || 30);
        setBidAmount(response.data.auction.currentBid + 1);
      }
    } catch (error) {
      console.error('Failed to load active auction:', error);
    }
  }, [draftId]);

  const completeAuction = useCallback(async () => {
    if (!auction) return;

    try {
      await apiClient.auctions.complete(auction._id);
    } catch (error) {
      console.error('Failed to complete auction:', error);
    }
  }, [auction]);

  // Load active auction
  useEffect(() => {
    loadActiveAuction();
  }, [draftId, loadActiveAuction]);

  // Update ref when auction changes
  useEffect(() => {
    auctionRef.current = auction;
  }, [auction]);

  // Timer countdown
  useEffect(() => {
    if (!auction || auction.status !== 'active') return;

    const interval = setInterval(() => {
      setTimeLeft(prev => {
        if (prev <= 1) {
          // Auction time expired
          completeAuction();
          return 0;
        }
        return prev - 1;
      });
    }, 1000);

    return () => clearInterval(interval);
  }, [auction, completeAuction]);

  // Socket listeners
  useEffect(() => {
    if (!socket || !isConnected) return;

    socket.on('auctionStarted', (data: AuctionStartedData) => {
      setAuction(data.auction);
      setTimeLeft(data.auction.timeLimit || 30);
      setBidAmount(data.auction.currentBid + 1);
    });

    socket.on('bidPlaced', (data: BidPlacedData) => {
      setAuction(data.auction);
      setTimeLeft(data.timeLeft);
      setBidAmount(data.auction.currentBid + 1);
      toast.success(`${data.bidderName} bid $${data.bidAmount}`);
    });

    socket.on('auctionCompleted', (data: AuctionCompletedData) => {
      setAuction(null);
      setTimeLeft(0);
      // Store the current auction before setting it to null
      setCompletedAuction(auctionRef.current);
      toast.success(`${data.winnerName} won ${data.playerName} for $${data.winningBid}`);
      onAuctionComplete?.();
    });

    socket.on('playerAssigned', (data: any) => {
      toast.success(`${data.player.name} assigned to ${data.team.name} for $${data.cost}`);
      onAuctionComplete?.();
    });

    return () => {
      socket.off('auctionStarted');
      socket.off('bidPlaced');
      socket.off('auctionCompleted');
      socket.off('playerAssigned');
    };
  }, [socket, isConnected, onAuctionComplete]);

  const placeBid = async () => {
    if (!auction || !userTeam) return;

    try {
      setLoading(true);
      const response = await apiClient.auctions.bid(auction._id, bidAmount, userTeam._id);
      
      if (response.success) {
        toast.success(`Bid placed: $${bidAmount}`);
      }
    } catch (error) {
      console.error('Failed to place bid:', error);
      toast.error('Failed to place bid');
    } finally {
      setLoading(false);
    }
  };

  const handleQuickBid = (amount: number) => {
    setBidAmount(amount);
  };

  const getBidTier = useCallback((amount: number) => {
    if (!userTeam) {
      return { tier: 'neutral' as const, label: 'No Team' };
    }

    const slotsRemaining = Math.max(20 - userTeam.players.length, 0);
    const reserve = Math.max(slotsRemaining - 1, 0);
    const targetSpend = Math.max(userTeam.budget.remaining - reserve, 0);

    if (amount <= targetSpend * 0.6) {
      return { tier: 'safe' as const, label: 'Safe' };
    }
    if (amount <= targetSpend) {
      return { tier: 'stretch' as const, label: 'Stretch' };
    }
    return { tier: 'overpay' as const, label: 'Overpay' };
  }, [userTeam]);

  const canBid = userTeam && 
    auction && 
    auction.status === 'active' && 
    userTeam.budget.remaining >= bidAmount &&
    userTeam._id !== auction.currentBidder?._id;



    return (
    <>
      {auction && (
        <AuctionContainer>
        <AuctionHeader>
          <AuctionTitle>
            <Gavel size={24} />
            Live Auction
          </AuctionTitle>
          <TimerDisplay timeLeft={timeLeft}>
            <Timer size={20} />
            {Math.floor(timeLeft / 60)}:{(timeLeft % 60).toString().padStart(2, '0')}
          </TimerDisplay>
        </AuctionHeader>

        <PlayerInfo>
          <PlayerDetails>
            <PlayerName>{auction.playerName}</PlayerName>
            <PlayerStats>
              <PositionBadge position={auction.position}>
                {auction.position}
              </PositionBadge>
              <span>{auction.nflTeam}</span>
              <span>Proj: {auction.projectedPoints?.toFixed(1)} pts</span>
            </PlayerStats>
                       {auction.nominatedBy && (
              <div style={{ 
                marginTop: spacing[2], 
                padding: spacing[2], 
                background: colors.grey[700], 
                borderRadius: borderRadius.base,
                fontSize: typography.fontSize.sm,
                color: colors.grey[300]
              }}>
                <strong>Nominated by:</strong> {auction.nominatedBy.name} (Team #{auction.nominatedBy.teamNumber})
              </div>
            )}
            
            {/* Team Dropdown */}
            <div style={{ 
              marginTop: spacing[3], 
              padding: spacing[3], 
              background: colors.grey[800], 
              borderRadius: borderRadius.base,
              border: `1px solid ${colors.grey[600]}`
            }}>
              <h4 style={{ 
                color: colors.white, 
                marginBottom: spacing[2], 
                fontSize: typography.fontSize.sm,
                fontWeight: typography.fontWeight.bold
              }}>
                Draft Teams
              </h4>
              <TeamsGrid>
                {teams.map((team) => (
                  <div key={team._id} style={{
                    padding: spacing[2],
                    background: colors.grey[700],
                    borderRadius: borderRadius.base,
                    border: auction.nominatedBy?._id === team._id ? `2px solid ${colors.green}` : `1px solid ${colors.grey[600]}`,
                    fontSize: typography.fontSize.xs
                  }}>
                    <div style={{ 
                      color: colors.white, 
                      fontWeight: typography.fontWeight.bold,
                      marginBottom: spacing[1]
                    }}>
                      {team.name} (Team #{team.teamNumber})
                    </div>
                    <div style={{ color: colors.grey[400] }}>
                      Budget: ${team.budget.remaining} remaining
                    </div>
                    <div style={{ color: colors.grey[400] }}>
                      Players: {team.players.length}/20
                    </div>
                    {auction.nominatedBy?._id === team._id && (
                      <div style={{ 
                        color: colors.green, 
                        fontWeight: typography.fontWeight.bold,
                        marginTop: spacing[1]
                      }}>
                        ← Nominating Team
                      </div>
                    )}
                  </div>
                ))}
              </TeamsGrid>
            </div>
         </PlayerDetails>
        </PlayerInfo>

        <CurrentBidSection>
          <CurrentBidAmount>${auction.currentBid}</CurrentBidAmount>
          <CurrentBidder>
            {auction.currentBidder ? (
              <>
                <User size={16} />
                {auction.currentBidder.name} (Team #{auction.currentBidder.teamNumber})
              </>
            ) : (
              'No bids yet'
            )}
          </CurrentBidder>
        </CurrentBidSection>

        {auction.bids && auction.bids.length > 0 && (
          <BidHistory>
            <h4 style={{ color: colors.white, marginBottom: spacing[3] }}>Bid History</h4>
            {auction.bids.slice(-5).reverse().map((bid, index) => (
              <BidItem key={index}>
                <span>{bid.teamId.name} (Team #{bid.teamId.teamNumber})</span>
                <span style={{ color: colors.green, fontWeight: 'bold' }}>
                  ${bid.amount}
                </span>
              </BidItem>
            ))}
          </BidHistory>
        )}

        {userTeam && (
          <div>
            {(() => {
              const bidTier = getBidTier(bidAmount);

              return (
                <BidInsight>
                  Bid Quality
                  <InsightBadge $tier={bidTier.tier}>{bidTier.label}</InsightBadge>
                </BidInsight>
              );
            })()}

            <BidForm>
              <BidInput
                type="number"
                value={bidAmount}
                onChange={(e) => setBidAmount(parseInt(e.target.value) || 1)}
                min={auction.currentBid + 1}
                max={userTeam.budget.remaining}
              />
              <Button
                onClick={placeBid}
                disabled={!canBid || loading}
                size="lg"
              >
                {loading ? 'Placing Bid...' : `Bid $${bidAmount}`}
              </Button>
            </BidForm>

            <QuickBidButtons>
              {[1, 2, 5, 10].map(amount => (
                <QuickBidButton
                  key={amount}
                  variant="secondary"
                  size="sm"
                  onClick={() => handleQuickBid(auction.currentBid + amount)}
                  disabled={auction.currentBid + amount > userTeam.budget.remaining}
                >
                  +${amount}
                </QuickBidButton>
              ))}
              <QuickBidButton
                variant="secondary"
                size="sm"
                onClick={() => handleQuickBid(userTeam.budget.remaining)}
                disabled={userTeam.budget.remaining <= auction.currentBid}
              >
                Max
              </QuickBidButton>
              <QuickBidButton
                variant="secondary"
                size="sm"
                onClick={() => {
                  const slotsRemaining = Math.max(20 - userTeam.players.length, 0);
                  const reserve = Math.max(slotsRemaining - 1, 0);
                  const smartBid = Math.max(auction.currentBid + 1, userTeam.budget.remaining - reserve);
                  handleQuickBid(Math.min(userTeam.budget.remaining, smartBid));
                }}
                disabled={userTeam.budget.remaining <= auction.currentBid}
              >
                Smart
              </QuickBidButton>
            </QuickBidButtons>

            <div style={{ 
              marginTop: spacing[3], 
              fontSize: typography.fontSize.sm, 
              color: colors.grey[400] 
            }}>
              Your budget: ${userTeam.budget.remaining} remaining
            </div>
          </div>
        )}
        </AuctionContainer>
      )}

      {/* Team Assignment Section */}
      {completedAuction && (
        <div style={{ marginTop: spacing[6] }}>
          <div style={{ 
            display: 'flex', 
            justifyContent: 'space-between', 
            alignItems: 'center',
            marginBottom: spacing[4],
            padding: spacing[4],
            background: colors.grey[800],
            borderRadius: borderRadius.md,
            border: `1px solid ${colors.grey[700]}`
          }}>
            <div>
              <h3 style={{ color: colors.white, marginBottom: spacing[2] }}>
                Auction Completed: {completedAuction.playerName}
              </h3>
              <p style={{ color: colors.grey[400], fontSize: typography.fontSize.sm }}>
                Winner: {completedAuction.currentBidder?.name} (Team #{completedAuction.currentBidder?.teamNumber}) - ${completedAuction.currentBid}
              </p>
            </div>
            <Button
              variant="secondary"
              onClick={() => setShowTeamAssignment(!showTeamAssignment)}
            >
              {showTeamAssignment ? 'Hide' : 'Show'} Team Assignment
            </Button>
          </div>

          {showTeamAssignment && (
            <TeamAssignment
              player={{
                _id: completedAuction.playerId,
                sportRadarId: completedAuction.playerId,
                name: completedAuction.playerName,
                position: completedAuction.position,
                team: completedAuction.nflTeam,
                projectedPoints: completedAuction.projectedPoints,
                cost: completedAuction.currentBid,
                isDrafted: false
              } as Player}
              teams={teams}
              draftId={draftId}
              onPlayerAssigned={(assignedPlayer: Player) => {
                setShowTeamAssignment(false);
                setCompletedAuction(null);
                onAuctionComplete?.();
                
                // Refresh the page after a short delay to ensure the player list is updated
                setTimeout(() => {
                  window.location.reload();
                }, 1000);
              }}
              onCancel={() => setShowTeamAssignment(false)}
            />
          )}
        </div>
      )}
    </>
  );
};

export default AuctionBidding;
